<script setup lang="ts">
import { computed, onUnmounted, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useDataRefreshRuntime } from '@/app/data-refresh';
import {
  chunkLoadingErrorState,
  reloadAfterChunkLoadingError,
} from '@/app/providers/router/chunk-loading-error-handler';
import { usePwaRuntime } from '@/app/pwa';
import { useSession } from '@/features/session';
import { isPlatformHost } from '@/shared/lib';
import { useAppVersion } from '@/shared/lib/app-version';
import { usePageReload } from '@/shared/lib/composables';
import AppVersionNotification from '@/shared/ui/AppVersionNotification.vue';
import { AppButton } from '@/shared/ui/button';
import ConfirmModal from '@/shared/ui/ConfirmModal.vue';
import { IconLogo } from '@/shared/ui/icons';
import MaintenanceScreen from '@/shared/ui/MaintenanceScreen.vue';
import { AppSpinner } from '@/shared/ui/spinner';

const appBuildTime = computed(() => {
  return __APP_BUILD_TIME__;
});

usePwaRuntime({
  pushEnabled: !isPlatformHost(globalThis.window?.location.origin ?? ''),
});
useDataRefreshRuntime();

const { hasNewVersion, isForced, reloadApp, dismiss, updateState, maintenanceActionLost } = useAppVersion();
const route = useRoute();
const router = useRouter();
const { status: sessionStatus, error: sessionError, initializeSession } = useSession();
const isRouterReady = shallowRef(router.currentRoute.value.matched.length > 0);

const removeRouterReadyHook = router.afterEach((_to, _from, failure) => {
  if (!failure) {
    isRouterReady.value = true;
  }
});

onUnmounted(removeRouterReadyHook);

const requiresSession = computed(() => route.meta.requiresAuth ?? true);
const isSessionLoading = computed(() => {
  return !isRouterReady.value || (requiresSession.value && sessionStatus.value === 'initializing');
});
const hasSessionError = computed(() => requiresSession.value && sessionStatus.value === 'error');
const sessionErrorMessage = computed(() => {
  return sessionError.value?.message ?? 'Не удалось загрузить данные сессии. Повторите попытку.';
});

const retrySession = async (): Promise<void> => {
  await initializeSession();

  if (sessionStatus.value === 'ready') {
    const requestedLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    await router.replace(requestedLocation);
  }
};

// Ключ мягкой перезагрузки: смена пересоздаёт активную страницу (клик по уже
// активному пункту меню). Не завязан на route — обычная навигация и смена query
// не трогают ключ, поэтому состояние страницы при выборе диалога сохраняется.
const { reloadKey } = usePageReload();
</script>

<template>
  <MaintenanceScreen
    v-if="updateState === 'maintenance'"
    :action-lost="maintenanceActionLost"
  />
  <div
    v-else-if="updateState !== 'ready'"
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-(--base-main-bg) p-4 text-(--base-text-color)"
    role="alert"
  >
    <section
      class="flex w-full max-w-xl flex-col items-center gap-4 rounded-3xl border border-white/8 bg-(--base-container-bg) p-7 text-center sm:p-10"
    >
      <IconLogo
        :width="56"
        :height="56"
        class="mb-2 text-(--sidebar-logo-color)"
      />
      <p>
        {{
          updateState === 'failed'
            ? 'Не удалось автоматически обновить приложение.'
            : 'Приложение обновляется. Подождите немного.'
        }}
      </p>
      <AppButton
        v-if="updateState === 'failed'"
        class="min-h-11"
        @click="reloadApp"
        >Обновить страницу</AppButton
      >
    </section>
  </div>
  <div class="min-h-screen bg-(--base-main-bg) text-(--base-text-color)">
    <AppVersionNotification
      :is-visible="hasNewVersion"
      :is-forced="isForced"
      @reload="reloadApp"
      @dismiss="dismiss"
    />
    <ConfirmModal />

    <div
      v-if="chunkLoadingErrorState"
      role="alert"
      class="flex min-h-screen items-center justify-center p-4"
    >
      <div class="flex max-w-md flex-col items-center gap-4 rounded-xl bg-(--base-container-bg) p-4 text-center md:p-6">
        <p>{{ chunkLoadingErrorState.message }}</p>
        <AppButton
          class="min-h-11"
          @click="reloadAfterChunkLoadingError"
        >
          Обновить страницу
        </AppButton>
      </div>
    </div>

    <div
      v-else-if="hasSessionError"
      role="alert"
      class="flex min-h-screen items-center justify-center p-4"
    >
      <div class="flex max-w-md flex-col items-center gap-4 rounded-xl bg-(--base-container-bg) p-4 text-center md:p-6">
        <p>{{ sessionErrorMessage }}</p>
        <AppButton
          class="min-h-11"
          @click="retrySession"
        >
          Повторить
        </AppButton>
      </div>
    </div>

    <div
      v-else-if="isSessionLoading"
      role="status"
      aria-live="polite"
      class="flex min-h-screen items-center justify-center gap-3"
    >
      <AppSpinner
        aria-hidden="true"
        size="lg"
      />
      <span class="sr-only">Загрузка приложения</span>
    </div>

    <router-view
      v-else
      v-slot="{ Component }"
    >
      <Suspense>
        <component
          :is="Component"
          :key="reloadKey"
        />

        <template #fallback>
          <div
            role="status"
            aria-live="polite"
            class="flex min-h-screen items-center justify-center gap-3"
          >
            <AppSpinner
              aria-hidden="true"
              size="lg"
            />
            <span class="sr-only">Загрузка страницы</span>
          </div>
        </template>
      </Suspense>
    </router-view>

    <div class="mt-4 ml-4 hidden text-xs text-gray-400">Last builded at: {{ appBuildTime }}</div>
  </div>
</template>
